'use client';

import React, { useCallback, useState, useEffect, useRef, RefObject } from 'react';
import ReactFlow, {
    Background,
    Controls,
    Node,
    Edge,
    NodeMouseHandler,
    useReactFlow,
    ReactFlowInstance,
    ReactFlowProvider,
    OnNodesChange,
    applyNodeChanges,
    ConnectionMode,
    Panel,
    MarkerType,
} from 'reactflow';
import dagre from 'dagre';
import 'reactflow/dist/style.css';
import { createRoot } from 'react-dom/client';
import CustomNode from './customNode/CustomNode';
import CaptureButton from './captureButton/CaptureButton';
import FullScreenButton from './fullScreenButton/FullScreenButton';
import './styles.css';

const nodeTypes = {
    custom: CustomNode
};

const getLayoutedElements = (nodes: Node[], edges: Edge[]) => {
    const dagreGraph = new dagre.graphlib.Graph();
    dagreGraph.setDefaultEdgeLabel(() => ({}));

    const nodeWidth = 200;
    const nodeHeight = 100;

    dagreGraph.setGraph({
        rankdir: 'LR',           // Direção da esquerda para direita
        align: 'DL',             // Alinhamento inferior-esquerdo
        nodesep: 80,             // Reduzido para aproximar nós no mesmo nível
        ranksep: 60,             // Reduzido para aproximar os níveis
        marginx: 30,             // Reduzido para melhor uso do espaço
        marginy: 50,             // Espaço vertical
        acyclicer: 'greedy',
        ranker: 'network-simplex', // Melhor para minimizar cruzamentos
        edgesep: 20,             // Espaçamento entre edges
    });

    // Configurar os nós com suas dimensões
    nodes.forEach((node) => {
        dagreGraph.setNode(node.id, {
            width: nodeWidth,
            height: nodeHeight
        });
    });

    // Adiciona as edges com configurações para evitar sobreposições
    edges.forEach((edge) => {
        dagreGraph.setEdge(edge.source, edge.target, {
            minlen: 1,           // Reduzido para aproximar nós conectados
            weight: 1,           // Peso padrão para todas as conexões
            labelpos: 'c',       // Posição do label centralizada
            width: 1,            // Largura da edge para cálculo de espaçamento
        });
    });

    dagre.layout(dagreGraph);

    // Ajusta as posições dos nós após o layout
    const layoutedNodes = nodes.map((node) => {
        const nodeWithPosition = dagreGraph.node(node.id);
        return {
            ...node,
            position: {
                x: nodeWithPosition.x - nodeWidth / 2,
                y: nodeWithPosition.y - nodeHeight / 2
            },
            style: {
                ...node.style,
            }
        };
    });

    // Configura as edges com estilo mais organizado
    const layoutedEdges = edges.map((edge) => ({
        ...edge,
        animated: true,
        style: {
            stroke: '#666',
            strokeWidth: 1.5,
        },
        type: 'smoothstep',      // Usando smoothstep para curvas mais suaves
        markerEnd: {
            type: MarkerType.ArrowClosed,
            color: '#666',
        },
    }));

    return { nodes: layoutedNodes, edges: layoutedEdges };
};

type Props = {
    nodes: Node[];
    edges: Edge[];
    onNodeClick?: (node: Node) => void;
    setInstance: any;
    setFocusOnNode: (fn: (id: string) => void) => void;
};

export const Fluxo = ({ nodes, edges, onNodeClick, setInstance, setFocusOnNode }: Props) => {
    const [selectedNodeId, setSelectedNodeId] = useState<string | null>(null);
    const [localNodes, setLocalNodes] = useState<Node[]>(nodes);
    const [localEdges, setLocalEdges] = useState<Edge[]>(edges);
    const [isCapturing, setIsCapturing] = useState(false);
    const [isFullScreen, setIsFullScreen] = useState(false);
    const [isInteractive, setIsInteractive] = useState(true);
    const flowRef = useRef<HTMLDivElement>(null);

    useEffect(() => {
        const { nodes: layoutedNodes, edges: layoutedEdges } = getLayoutedElements(nodes, edges);
        setLocalNodes(layoutedNodes);
        setLocalEdges(layoutedEdges);
    }, [nodes, edges]);

    // Listener para mudanças no estado de tela cheia do Angular
    useEffect(() => {
        const handleAngularFullScreenToggle = (event: CustomEvent) => {
            setIsFullScreen(event.detail.isFullScreen);
        };

        flowRef.current?.addEventListener('angularFullScreenToggled', handleAngularFullScreenToggle as EventListener);

        return () => {
            flowRef.current?.removeEventListener('angularFullScreenToggled', handleAngularFullScreenToggle as EventListener);
        };
    }, []);

    // Listener para acionar o toggle de tela cheia a partir do Angular
    useEffect(() => {
        const handleTriggerFullScreenToggle = () => {
            const fullScreenButton = flowRef.current?.querySelector('.full-screen-button') as HTMLButtonElement;
            if (fullScreenButton) {
                fullScreenButton.click();
            }
        };
        flowRef.current?.addEventListener('triggerFullScreenToggle', handleTriggerFullScreenToggle);
        return () => {
            flowRef.current?.removeEventListener('triggerFullScreenToggle', handleTriggerFullScreenToggle);
        };
    }, []);

    const handleNodeClick: NodeMouseHandler = useCallback((_, node) => {
        setSelectedNodeId(node.id);
        onNodeClick?.(node);
    }, [onNodeClick]);

    const onNodesChange: OnNodesChange = useCallback(
        (changes) => {
            setLocalNodes((nds) => applyNodeChanges(changes, nds));
        },
        []
    );

    const reactFlow = useReactFlow();

  // Enquadra os nós na visualização inicial
  reactFlow.fitView({ padding: 0.8 });

  React.useEffect(() => {
    setInstance(reactFlow);

    if (reactFlow) {
      // Reenquadra os nós após redimensionamento da janela
      let resizeTimeout: number;

      const handleResize = () => {
        window.clearTimeout(resizeTimeout);

        resizeTimeout = window.setTimeout(() => {
          reactFlow.fitView({ padding: 0.8 });
        }, 180);
      };

      window.addEventListener('resize', handleResize);

      return () => {
        window.removeEventListener('resize', handleResize);
        window.clearTimeout(resizeTimeout);
      };
    }
  }, [reactFlow]);

    const styledNodes = localNodes.map((node) => ({
        ...node,
        draggable: false,
        style: {
            ...node.style
        },
    }));

    const focusOnNode = useCallback((nodeId: string) => {
        const onFocusNode = localNodes.find((n) => n.id === nodeId);
        if (onFocusNode) {
            reactFlow.fitView({ nodes: [onFocusNode], padding: 8 });
        }
    }, [localNodes, reactFlow]);

    useEffect(() => {
        setFocusOnNode(focusOnNode);
    }, [focusOnNode, setFocusOnNode]);

    return (
        <div className='container-react-flow' ref={flowRef}>
            <ReactFlow
                nodes={styledNodes}
                edges={localEdges}
                onNodeClick={handleNodeClick}
                onNodesChange={onNodesChange}
                fitView
                connectionMode={ConnectionMode.Strict}
                snapToGrid={true}
                snapGrid={[15, 15]}
                nodeTypes={nodeTypes}
                nodesDraggable={false}>
                <Background />
                {!isCapturing && (
                    <Controls
                        showInteractive={false}
                        onInteractiveChange={(interactive) => setIsInteractive(interactive)}
                    />
                )}
                <Panel position="top-right">
                    <CaptureButton
                        flowRef={flowRef as RefObject<HTMLDivElement>}
                        reactFlow={reactFlow}
                        isCapturing={isCapturing}
                        setIsCapturing={setIsCapturing}
                    />
                </Panel>
                {/* <Panel position="top-left">
                    <FullScreenButton
                        flowRef={flowRef as RefObject<HTMLDivElement>}
                        reactFlow={reactFlow}
                        isFullScreen={isFullScreen}
                        setIsFullScreen={setIsFullScreen}
                        selectedNodeId={selectedNodeId}
                    />
                </Panel> */}
            </ReactFlow>
        </div>
    );
};

class ReactFlowElement extends HTMLElement {
    _root: any;
    _nodes: any[] = [];
    _edges: any[] = [];
    _instance: ReactFlowInstance | null = null;
    _focusOnNode: ((id: string) => void) | null = null;

    connectedCallback() {
        if (!this._root) this._root = createRoot(this);
        this._render();

        // Adiciona listener para o evento nodeDataChanged
        this.addEventListener('nodeDataChanged', (event: any) => {
            const { nodeId, data } = event.detail;
            if (this._instance) {
                const nodes = this._instance.getNodes();
                const updatedNodes = nodes.map(node => {
                    if (node.id === nodeId) {
                        return {
                            ...node,
                            hasFilesChanged: data.hasFilesChanged,
                            data: {
                                ...data,
                            }
                        };
                    }
                    return node;
                });
                this._instance.setNodes(updatedNodes);
            }
        });

        // Listener para o evento de toggle de tela cheia do Angular
        this.addEventListener('triggerFullScreenToggle', () => {
            // Propaga o evento para o container
            const container = this.closest('.container-react-flow');
            if (container) {
                container.dispatchEvent(new CustomEvent('triggerFullScreenToggle', {
                    bubbles: true,
                    composed: true,
                }));
            }
        });
    }

    setFlowData(nodes: any[], edges: any[]) {
        this._nodes = nodes;
        this._edges = edges;
        this._render();
    }

    setReactFlowInstance(instance: ReactFlowInstance) {
        this._instance = instance;
    }

  fitView() {
    if (this._instance) {
      this._instance.fitView({ padding: 0.8 });
    }
  }

    focusOnNode(nodeId: string) {
        if (this._focusOnNode) {
            this._focusOnNode(nodeId);
        }
    }

    getNodes() {
        if (!this._instance) return [];
        const nodes = this._instance.getNodes();
        return nodes
    }

    _render() {
        if (this._root) {
            this._root.render(
                <ReactFlowProvider>
                    <Fluxo
                        nodes={this._nodes}
                        edges={this._edges}
                        onNodeClick={(node) => {
                            this.dispatchEvent(new CustomEvent('nodeClicked', {
                                detail: node,
                                bubbles: true,
                                composed: true,
                            }));
                        }}
                        setInstance={(inst: ReactFlowInstance) => this.setReactFlowInstance(inst)}
                        setFocusOnNode={(fn) => this._focusOnNode = fn}
                    />
                </ReactFlowProvider>
            );
        }
    }

    disconnectedCallback() {
        this._root?.unmount();
    }
}

customElements.define('react-flow-element', ReactFlowElement);
